import { Form, Spin } from 'antd';
import RadioCards from 'components/RadioCards/RadioCards';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { familyService } from 'services';
import { getChildLastStep, getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import {
  FAMILY_APPLICATION_STEPS,
  CHILD_PROVIDER_SITUATION_OPTIONS,
  CHILD_PROVIDER_SITUATION_VALUES,
} from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { Banner } from 'components/ui/Banner';
import { useAtomValue } from 'jotai';
import { getFirstName } from 'utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyApplicationChildrenProviderConnection({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);
  const location = useLocation();

  // Form state
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Application
  const isCompletedBySponsor = application?.completedBySponsor === true;

  // Get entity index from query parameters
  const urlParams = new URLSearchParams(location.search);
  const currentChildIndex = parseInt(urlParams.get('entity') || '0', 10);

  const currentChild = application?.children?.[currentChildIndex];
  const childFirstName = getFirstName({ firstName: currentChild?.firstName });

  // Initialize form with existing data
  useEffect(() => {
    if (currentChild) {
      form.setFieldsValue({
        providerSituation: currentChild.providerSituation,
      });
    }
  }, [currentChild, form]);

  const handleSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication, _newChild) => {
        const _childLastStep = getChildLastStep(_newChild);

        // Navigate to next step
        const nextStep = getNextStep(
          FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_CONNECTION,
          _newApplication,
          stepConfig,
          _childLastStep === FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_CONNECTION
            ? currentChildIndex === _newApplication?.children?.length - 1
              ? null
              : currentChildIndex
            : currentChildIndex,
        );
        setStep({
          applicationId: _newApplication.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        // Update child with provider situation
        const childData = {
          ...currentChild,
          providerSituation: values.providerSituation,
          // Reset careStartDate and careStartDateUnknown fields if provider situation is InCare
          // FAMILY-APPS__DATA_CLEANING => CHILDREN_CHILD_CARE_NEEDS step
          careStartDate:
            values.providerSituation === CHILD_PROVIDER_SITUATION_VALUES.InCare ? null : currentChild.careStartDate,
          careStartDateUnknown:
            values.providerSituation === CHILD_PROVIDER_SITUATION_VALUES.InCare
              ? null
              : currentChild.careStartDateUnknown,
        };

        // If provider situation is ReferralNeeded
        // remove all existing providers for this child
        if (values.providerSituation === CHILD_PROVIDER_SITUATION_VALUES.ReferralNeeded) {
          try {
            // Get all existing providers for the application
            const existingProviders = await familyService.getApplicationProviders(application.id);

            // Find providers that belong to the current child
            const childProviders = existingProviders.filter((provider) => provider.childId === currentChild?.id);

            // Delete all providers for this child
            for (const provider of childProviders) {
              try {
                await familyService.deleteApplicationProvider(application.id, provider.id);
              } catch (error) {
                console.error('Error deleting provider:', provider.id, error);
              }
            }
          } catch (error) {
            console.error('Error getting or deleting providers:', error);
          }
        }

        // Save child
        const savedChild = await familyService.addUpdateFamilyChild(household?.id, currentChild?.id || null, childData);

        // Update application with the saved child
        const updatedChildren = [...(application.children || [])];
        updatedChildren[currentChildIndex] = savedChild;

        const updatedApplication = {
          ...application,
          children: updatedChildren,
        };

        const savedApplication = await familyService.saveApplication(updatedApplication);
        setApplication(savedApplication);

        _goToNextStep(savedApplication, savedChild);
      } catch (error) {
        console.error('Error saving child provider situation:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, currentChild, currentChildIndex, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={submitting}>
      <PageMeta title={`${childFirstName} - Provider Connection`} />

      <Form form={form} onFinish={handleSubmit} layout="vertical" disabled={readOnly}>
        <div className="p-7 pt-0">
          <div className="space-y-6">
            <Banner question={`Do you have a provider for ${childFirstName}?`} className="my-12" />

            <Form.Item
              name="providerSituation"
              rules={[{ required: true, message: 'Please select a child care situation' }]}
            >
              <RadioCards
                options={CHILD_PROVIDER_SITUATION_OPTIONS.map((option) => ({
                  value: option.value,
                  label: isCompletedBySponsor
                    ? option.label.completedBySponsor
                    : option.label.completedOnBehalfOfSponsor,
                }))}
                data-testid="child-provider-situation-cards"
              />
            </Form.Item>
          </div>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            onClick: () => {
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_CONNECTION,
                application,
                stepConfig,
                currentChildIndex,
              );
              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
            disabled: submitting,
            'data-testid': 'children-provider-connection-back-button',
          }}
          nextButtonProps={{
            loading: submitting,
            'data-testid': 'children-provider-connection-next-button',
          }}
        />
      </Form>
    </Spin>
  );
}
